import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

// Action imports
import { fetchAllPublicEvents } from "../../redux/posts/posts.action";

// Component imports
import EventsCards from "./EventsCards";
import CreateEvent from "../Modal/CreateEventModal";
import PreviewEvent from "../Modal/PreviewEventModal";
import EmptyCard from "./EmptyCards";
import Loader from "../Loader";

// Asset imports
import EmptyStateHosting from "../../assets/images/Frame.svg";

// Utility imports
import { getGoogleLocation } from "../../utils/utils";
import EmptyStateComponent from "../EmptyStateCard";

// Tab constants for better maintainability
const TABS = {
  UPCOMING: 1,
  HOSTING: 2,
  GOING: 3,
  PAST_EVENTS: 4,
};

function EventsTab() {
  const dispatch = useDispatch();

  // Redux state selectors
  const darktheme = useSelector((state) => state.features.settings.darktheme);
  const events = useSelector((state) => state.posts.publicEvents);
  const loading = useSelector((state) => state.posts.publicEventsLoading);
  const { user } = useSelector((state) => state.auth);

  // Local state management
  const [activeTab, setActiveTab] = useState(TABS.UPCOMING);
  const [todaysDate] = useState(new Date());
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [currentLocation, setCurrentLocation] = useState({
    longitude: 0,
    latitude: 0,
  });
  const [filteredPastEvents, setPastEvents] = useState([]);

  // Fetch user's current location on component mount
  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const { lat, lng } = await getGoogleLocation();
        setCurrentLocation({
          latitude: lat,
          longitude: lng,
        });
      } catch (error) {
        console.error("Error fetching location:", error);
      }
    };

    fetchLocation();
  }, []);

  // Fetch events when location is available
  useEffect(() => {
    if (
      currentLocation.latitude !== null &&
      currentLocation.longitude !== null
    ) {
      getFeeds();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocation]);

  // Filter past events when events data changes
  useEffect(() => {
    setPastEvents(
      events?.filter((item) => moment(item?.date).isBefore(moment(todaysDate)))
    );
  }, [events, todaysDate]);

  // API call to fetch events based on location
  const getFeeds = () => {
    const payload = {
      page: 1,
      lat: currentLocation?.latitude,
      lng: currentLocation?.longitude,
    };
    dispatch(fetchAllPublicEvents(payload));
  };

  // Modal handlers
  const closeModal = () => setShowCreateEventModal(false);

  const previewEvent = () => {
    setPreviewModalOpen(true);
    setShowCreateEventModal(false);
  };

  const closeEvent = () => {
    setPreviewModalOpen(false);
    setShowCreateEventModal(true);
  };

  // Filter functions for different event categories
  const getHostedEvents = () =>
    events?.filter((item) => item.userId === user?.userId);
  const getInterestedEvents = () => events?.filter((item) => item.interested);

  // Tab button configuration
  const tabButtons = [
    { id: TABS.UPCOMING, label: "Upcoming", width: "w-[78px]" },
    { id: TABS.HOSTING, label: "Hosting", width: "w-[68px]" },
    { id: TABS.GOING, label: "Going", width: "w-[54px]" },
    { id: TABS.PAST_EVENTS, label: "Past Events", width: "w-[90px]" },
  ];

  // Common button styles
  const getTabButtonClass = (tabId) => `
    ${
      activeTab === tabId
        ? `${darktheme ? "text-white bg-none" : `bg-[#F2F2F5] text-[#525866]`}`
        : `${
            darktheme ? `bg-[#F2F2F5] text-[#525866]` : "text-[#0A0D14] bg-none"
          }`
    } 
    ${tabButtons.find((tab) => tab.id === tabId)?.width} 
    h-[28px] font-[500] px-[10px] py-[8px] gap-[4px] flex items-center 
    rounded-[999px] border-[#E2E4E9] border-[1px] text-[#7E808A] text-[12px]
  `;

  // Render events grid with loading state
  const renderEventsGrid = (eventsList, showEmptyCard = false) => {
    if (loading) {
      return (
        <div className="mx-auto text-center mt-[4rem]">
          <Loader big={true} />
        </div>
      );
    }

    return (
      <div className="flex w-full flex-wrap mt-[15px] gap-[24px]">
        {showEmptyCard && (
          <EmptyCard createPost={() => setShowCreateEventModal(true)} />
        )}
        {eventsList?.map((item, index) => (
          <EventsCards
            key={item.id || index}
            data={item}
            hosted={activeTab === TABS.HOSTING}
          />
        ))}
      </div>
    );
  };

  return (
    <div className={`${darktheme ? "bg-kdark3" : "bg-white"}`}>
      {loading ? (
        <div className="mx-auto text-center mt-[4rem]">
          <Loader big={true} />
        </div>
      ) : (
        <>
          {/* Tab Navigation */}
          <div className="flex gap-[8px] mt-[12px] items-center">
            {tabButtons.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={getTabButtonClass(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {/* Upcoming Events Tab */}
          {activeTab === TABS.UPCOMING &&
            (loading ? (
              <div className="mx-auto text-center mt-[4rem]">
                <Loader big={true} />
              </div>
            ) : events?.length === 0 ? (
              <EmptyStateComponent
                title="There are no event around you currently"
                description="Hosting an event soon in your community? You can create events on arad."
                buttonText="Create Event"
                buttonAction={() => setShowCreateEventModal(true)}
                titleClass="w-full md:w-[248px] mx-auto my-[8px] text-[24px] font-[700] leading-[22px]"
              />
            ) : (
              renderEventsGrid(events)
            ))}

          {/* Hosting Tab */}
          {activeTab === TABS.HOSTING &&
            (loading ? (
              <div className="mx-auto text-center mt-[4rem]">
                <Loader big={true} />
              </div>
            ) : getHostedEvents()?.length === 0 ? (
              <EmptyStateComponent
                title="You have not created any events yet."
                description="Anyone in your community can view and attend your events."
                buttonText="Create Now"
                buttonAction={() => setShowCreateEventModal(true)}
                image={EmptyStateHosting}
                titleClass="w-[346px] mx-auto text-[20px] font-[700] leading-[28px]"
              />
            ) : (
              renderEventsGrid(getHostedEvents(), true)
            ))}

          {/* Going Tab */}
          {activeTab === TABS.GOING &&
            (loading ? (
              <div className="mx-auto text-center mt-[4rem]">
                <Loader big={true} />
              </div>
            ) : getInterestedEvents()?.length === 0 ? (
              <EmptyStateComponent
                title="You are not attending any events"
                description="You can see and attend upcoming event in your community through the upcoming tab."
                buttonText="View Upcoming Events"
                buttonAction={() => setActiveTab(TABS.UPCOMING)}
              />
            ) : (
              renderEventsGrid(getInterestedEvents())
            ))}

          {/* Past Events Tab */}
          {activeTab === TABS.PAST_EVENTS &&
            (loading ? (
              <div className="mx-auto text-center mt-[4rem]">
                <Loader big={true} />
              </div>
            ) : filteredPastEvents?.length === 0 ? (
              <EmptyStateComponent
                title="You have not attended any events yet."
                description="You can see and attend upcoming event in your community through the upcoming tab."
                buttonText="View Upcoming Events"
                buttonAction={() => setActiveTab(TABS.UPCOMING)}
                titleClass="w-[342px] mx-auto my-[8px] text-[24px] font-[700] leading-[20px]"
              />
            ) : (
              renderEventsGrid(filteredPastEvents)
            ))}

          {/* Modals */}
          {showCreateEventModal && (
            <CreateEvent
              isOpen={showCreateEventModal}
              setShowModal={closeModal}
              showPreview={previewEvent}
            />
          )}

          {previewModalOpen && (
            <PreviewEvent
              isOpen={previewModalOpen}
              closePreview={closeEvent}
              callBack={getFeeds}
              close={setPreviewModalOpen}
            />
          )}
        </>
      )}
    </div>
  );
}

export default EventsTab;
